import { Container, Graphics, Ticker } from 'pixi.js';

/** Distinct line colors per payline index, tuned against the gold/brown theme. */
export const LINE_COLORS = [
  0xffd45e, 0xff5e6c, 0x4ee0c2, 0xb98cff, 0x7ded6a,
  0xff9a3d, 0x5ea8ff, 0xff7ad9, 0xd6e85a, 0x64e6ff,
];

export function lineColor(index: number): number {
  return LINE_COLORS[index % LINE_COLORS.length];
}

const STUB = 14; // lead-in/out beyond the board edges so lines enter the frame
const DRAW_MS = 260;

interface Path {
  points: { x: number; y: number }[];
  /** Cumulative length up to each point (cum[0] = 0). */
  cum: number[];
  total: number;
}

/**
 * Draws payline paths through cell centers, above the reels. Board-space
 * coordinates only — the parent container's fit() scaling applies for free.
 */
export class PaylineOverlay {
  readonly view = new Container();
  private readonly g = new Graphics();
  private paths: Path[] = [];
  private ticker: Ticker;

  // Animation state (single active mode at a time).
  private mode: 'idle' | 'static' | 'draw' = 'idle';
  private drawIndex = 0;
  private drawT = 0;
  private drawDone: (() => void) | null = null;
  private readonly tick = (t: Ticker) => this.update(t.deltaMS);

  constructor(cell: number, reels: number, paylines: number[][], ticker: Ticker) {
    this.view.addChild(this.g);
    this.ticker = ticker;
    this.setPaylines(cell, reels, paylines);
    ticker.add(this.tick);
  }

  setPaylines(cell: number, reels: number, paylines: number[][]): void {
    this.paths = paylines.map((line) => {
      const points = line.map((row, col) => ({ x: (col + 0.5) * cell, y: (row + 0.5) * cell }));
      points.unshift({ x: -STUB, y: points[0].y });
      points.push({ x: reels * cell + STUB, y: points[points.length - 1].y });
      const cum = [0];
      for (let i = 1; i < points.length; i++) {
        cum.push(cum[i - 1] + Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y));
      }
      return { points, cum, total: cum[cum.length - 1] };
    });
  }

  /** Draw all given lines at once (the all-lines flash). */
  showLines(indices: number[]): void {
    this.finishDraw();
    this.mode = 'static';
    this.g.clear();
    for (const i of indices) this.strokePath(i, 1, 4);
  }

  /** Animate one line drawing on left→right; resolves when fully drawn (then holds with a pulse). */
  animateLine(index: number, ms = DRAW_MS): Promise<void> {
    this.finishDraw();
    this.mode = 'draw';
    this.drawIndex = index;
    this.drawT = 0;
    this.drawMs = ms;
    return new Promise((res) => {
      this.drawDone = res;
    });
  }

  private drawMs = DRAW_MS;

  clear(): void {
    this.finishDraw();
    this.mode = 'idle';
    this.g.clear();
  }

  destroy(): void {
    this.ticker.remove(this.tick);
    this.view.destroy({ children: true });
  }

  private finishDraw(): void {
    const done = this.drawDone;
    this.drawDone = null;
    done?.();
  }

  private update(dtMs: number): void {
    if (this.mode !== 'draw') return;
    this.drawT += dtMs;
    const t = Math.min(1, this.drawT / this.drawMs);
    const eased = 1 - Math.pow(1 - t, 3);
    this.g.clear();
    if (t >= 1) {
      // Fully drawn: hold with a subtle width pulse.
      const pulse = 4 + Math.sin(this.drawT / 130);
      this.strokePath(this.drawIndex, 1, pulse);
      this.finishDraw();
    } else {
      this.strokePath(this.drawIndex, eased, 4);
    }
  }

  /** Stroke path `index` up to fraction `frac` of its length: glow pass + core pass. */
  private strokePath(index: number, frac: number, coreWidth: number): void {
    const path = this.paths[index];
    if (!path) return;
    const color = lineColor(index);
    const maxLen = path.total * frac;

    const trace = () => {
      this.g.moveTo(path.points[0].x, path.points[0].y);
      for (let i = 1; i < path.points.length; i++) {
        if (path.cum[i] <= maxLen) {
          this.g.lineTo(path.points[i].x, path.points[i].y);
        } else {
          // Interpolate the final point along the current segment.
          const seg = path.cum[i] - path.cum[i - 1];
          const p = seg > 0 ? (maxLen - path.cum[i - 1]) / seg : 0;
          const a = path.points[i - 1];
          const b = path.points[i];
          this.g.lineTo(a.x + (b.x - a.x) * p, a.y + (b.y - a.y) * p);
          break;
        }
      }
    };

    trace();
    this.g.stroke({ width: 11, color, alpha: 0.22, cap: 'round', join: 'round' });
    trace();
    this.g.stroke({ width: coreWidth, color, alpha: 0.95, cap: 'round', join: 'round' });
  }
}
